<script setup>
import {ref, onMounted, computed, watch} from 'vue'
import { useDisplay } from "vuetify"
import ButtonSave from "@/components/common/button/ButtonSave.vue";
import {handleError} from "@/utils/errors.js";
import messageService from '@/services/messages.js'
import {useI18n} from "vue-i18n";
import router from "@/router/index.js";
import {useRoute} from "vue-router";
import {getAvailableActionsForRoute} from "@/services/api/permissionManagement.js";
import ActiveSwitch from "@/components/common/fields/ActiveSwitch.vue";
import {
    createAttributeLayout,
    getAttributeLayoutById,
    updateAttributeLayout
} from "@/services/api/attributeLayout.js";
import InputText from "@/components/common/fields/InputText.vue";
import Textarea from "@/components/common/fields/Textarea.vue";
import AttributeLayoutForm from "@/components/isp/AttributeLayoutForm.vue";
import ButtonPreviousNext from "@/components/common/button/ButtonPreviousNext.vue";
import {FormLayout} from "@/enums/enums.js";
import BasePage from "@/components/Layouts/BasePage.vue";
import {LayoutEntity} from "@/entities/LayoutEntity.js";
import BaseTable from "@/components/tables/BaseTable.vue";

const { t } = useI18n();
const loading = ref(false);
const isMounted = ref(false);
const name = ref('');
const description = ref('');
const isEdit = computed(() => !!route.params.id)
const route = useRoute();
const actionsPermission = ref(null);
const disabledInput = ref(false);
const havePermission = ref(false);
const isActive = ref(false);
const form = ref(null)
const read_only = ref(false);
const step = ref(1);
const totalItems = ref(0);
const currentPage = ref(1);
const itemsPerPage = ref(10);
const itemsCheck = ref([]);
const itemsReply = ref([]);
const showFormCheck = ref(false);
const showFormReply = ref(false);
const entity = new LayoutEntity();

const actionsItensList = {
    "delete": "delete"
}

const requiredRule = (value) => !!value || t('common.messages.validations.required');
const { smAndDown } = useDisplay()

onMounted(async () => {
    try {
        actionsPermission.value = await getAvailableActionsForRoute("Resources/ISP/Attribute");

        if (!actionsPermission.value?.edit?.length ) {
            disabledInput.value = true;
        }
        if (actionsPermission.value?.view?.length) {
            havePermission.value = true;
        } else {
            message.error(t("common.messages.errors.403"));
        }
        if (isEdit.value) {
            const {data} = await getAttributeLayoutById({id: route.params.id});
            name.value = data.name;
            description.value = data.description;
            if (data.attributes) {
                const attributesArray = Array.isArray(data.attributes)
                    ? data.attributes
                    : Object.values(data.attributes);

                itemsCheck.value = attributesArray.filter(item => item.stCheckReply === 'Check');
                itemsReply.value = attributesArray.filter(item => item.stCheckReply === 'Reply');
            }
            isActive.value = data.status;
            if(data.read_only){
                read_only.value = data.read_only;
                disabledInput.value = true;
            }
        } else {
            isActive.value = true;
        }
    }
    catch (error) {
        message.error(await handleError(error, t));
        console.error(error);
    } finally {
        isMounted.value = true;
    }
})

const handleDelete = (selectedAttribute) => {
    if (step.value === FormLayout.Check){
        itemsCheck.value = itemsCheck.value.filter(item => item.id !== selectedAttribute.id);
    } else {
        itemsReply.value = itemsReply.value.filter(item => item.id !== selectedAttribute.id);
    }
};

const addItem = (item) => {
    step.value === FormLayout.Check ? itemsCheck.value.push(item) : itemsReply.value.push(item);
}

function _rowClass(row) {
    const state = row?.clientaccessState;
    return {
        "bg-green-50": state === "Normal",
        "bg-yellow-50": state === "Warning",
        "bg-red-50": state === "Blocked",
        "bg-grey-50": state === "Suspended",
    };
}

const mappedItemsCheck = useMappedItems(itemsCheck);
const mappedItemsReply = useMappedItems(itemsReply);

function usePagination(itemsRef, currentPage, itemsPerPage) {
    const paginatedItems = computed(() => {
        const page = currentPage.value;
        const perPage = itemsPerPage.value;

        const start = (page - 1) * perPage;
        const end = start + perPage;

        return itemsRef.value.slice(start, end);
    });

    const totalInfo = computed(() => {
        const totalItems = itemsRef.value.length;

        return {
            totalItems,
            totalPages: Math.ceil(totalItems / itemsPerPage.value),
            limit: itemsPerPage.value,
            page: currentPage.value,
        };
    });

    return {
        paginatedItems,
        totalInfo,
    };
}

const { paginatedItems: paginatedItemsCheck, totalInfo: totalItemsCheck } =
    usePagination(mappedItemsCheck, currentPage, itemsPerPage);

const { paginatedItems: paginatedItemsReply, totalInfo: totalItemsReply } =
    usePagination(mappedItemsReply, currentPage, itemsPerPage);

function onPageChange(page) {
    if (!page) return;
    currentPage.value = page;
}

function useMappedItems(sourceRef) {
    return computed(() => {
        if (!Array.isArray(sourceRef.value)) return [];

        const mapped = sourceRef.value.map((c = {}) => {
            const operator =
                [c.radiusOperatorListCheck, c.radiusOperatorListReply, c.operator]
                    .map(v => (typeof v === "string" ? v.trim() : ""))
                    .find(v => v !== "") || "";

            return {
                id: c.id ?? "",
                sequence: Number(c.sequence) || 0,
                name: c.attribute?.title || c.attribute || "",
                operator,
                value: c.value ?? "",
                st_check_reply: c.st_check_reply ?? "",
                clientaccessState: c.clientaccessState ?? "",
                client_state: c.clientaccessState ?? "",
                connection_type: c.connection_type ?? "",
                disabled: read_only.value,
            };
        });

        return mapped.sort((a, b) => {
            if (a.clientaccessState < b.clientaccessState) return -1;
            if (a.clientaccessState > b.clientaccessState) return 1;
            return a.sequence - b.sequence;
        });
    });
}

watch(
    () => step.value,
    () => {
        currentPage.value = 1;
    }
);

const goToStep = async (nextStep) => {
    const result = await form.value.validate()

    if (result.valid) {
        step.value = nextStep
    }
}

function getTranslate(label){
    return label !== '' ? t('common.labels.'+ label) : '';
}
const handleSubmit = async () => {
    const { valid } = await form.value.validate();
    if (!valid) return message.error(t('common.messages.errors.400'));

    loading.value = true;

    try {
        const payload = {
            name: name.value,
            description: description.value,
            attributes: [
                ...mappedItemsCheck.value,
                ...mappedItemsReply.value
            ]
        };

        if (isEdit.value) {
            await updateAttributeLayout({ id: route.params.id, ...payload, status: isActive.value});
            messageService.success(t('common.messages.success.update'));
        } else {
            await createAttributeLayout(payload);
            messageService.success(t('common.messages.success.save'));
        }
        router.push('/isp/layouts');
    } catch (error) {
        console.error(error);
        message.error(await handleError(error.response, t, name.value));

    } finally {
        loading.value = false
    }
}

</script>

<template>
    <BasePage :loading-mounted="isMounted" :entity="entity">
    <v-card class="bg-white trezo-card px-sm-0">
            <v-stepper v-model="step" :direction="smAndDown ? 'vertical' : 'horizontal'">
                <v-stepper-header :class="{ 'flex-wrap': smAndDown }" class="overflow-x-auto">
                    <v-stepper-item :title="t('common.labels.generalDetails')" @click.stop="step = 1" :value="1" />
                    <v-divider></v-divider>
                    <v-stepper-item :title="t('components.isp.layout.checkAttributes')" @click.stop="step = FormLayout.Check" :value="2" />
                    <v-divider></v-divider>
                    <v-stepper-item :title="t('components.isp.layout.replyAttributes')" @click.stop="step = FormLayout.Reply" :value="3" />
                </v-stepper-header>

                <v-stepper-window class="px-sm-0">
                    <!-- Etapa 1 -->
                    <v-stepper-window-item :value="1">
                        <v-form
                            v-if="isMounted"
                            ref="form"
                            validate-on="invalid-input"
                            @submit.prevent="handleSubmit"
                            class="bg-white"
                        >
                            <div class="row">
                                <div class="col-sm-6">
                                    <div class="trezo-form-group">
                                        <v-label class="main-label d-block fw-medium text-black">
                                            {{ t('common.labels.name') }}
                                        </v-label>
                                        <InputText
                                            v-model="name"
                                            :rules="[requiredRule]"
                                            :label="t('common.labels.name')"
                                            :readonly="disabledInput"
                                            required
                                        />
                                    </div>
                                </div>

                                <div class="col-sm-6">
                                    <div class="trezo-form-group">
                                        <v-label class="main-label d-block fw-medium text-black">
                                            {{ t('common.labels.description') }}
                                        </v-label>
                                        <Textarea
                                            :readonly="disabledInput"
                                            v-model="description"
                                            :rules="[requiredRule]"
                                            :label="t('common.labels.description')"
                                            required
                                        />
                                    </div>
                                </div>

                                <v-col cols="12" class="mt-3">
                                    <ActiveSwitch
                                        v-if="isMounted"
                                        :disabled="!isEdit"
                                        v-model="isActive"
                                    />
                                </v-col>
                            </div>

                        </v-form>
                        <div class="d-flex justify-end mt-4">
                            <ButtonPreviousNext  :next="true" @click="goToStep(2)" />
                        </div>
                    </v-stepper-window-item>

                    <v-stepper-window-item :value="2">
                        <AttributeLayoutForm  :formValue="FormLayout.Check" v-if="showFormCheck" @add-item="addItem" />
                        <div class="col-12 d-flex justify-start mb-5">
                            <v-btn
                                color="primary"
                                icon
                                class="rounded-circle"
                                v-if="!showFormCheck && !disabledInput"
                                @click="showFormCheck=true"
                            >
                                <v-icon> mdi-plus</v-icon>
                            </v-btn>
                        </div>
                        <BaseTable
                            :fetchData="null"
                            :item-class="_rowClass"
                            tableToUse="tableColumnsAttributes"
                            :entity="entity"
                            :items="paginatedItemsCheck"
                            :loading="loading"
                            :totalItems="totalItemsCheck"
                            :currentPage="currentPage"
                            :itemsPerPage="itemsPerPage"
                            :actions="actionsItensList"
                            @delete="handleDelete"
                            @page-change="onPageChange"
                        >
                            <template #clientaccessState="{ row }">
                                <span>
                                    {{ getTranslate(row.clientaccessState) }}
                                </span>
                            </template>
                        </BaseTable>
                        <v-row cols="12" class="mt-10 " >
                            <div class="col-6 d-flex justify-start">
                                <ButtonPreviousNext size="sm" :next="false" @click="step = 1" />
                            </div>
                            <div class="col-6 d-flex justify-end">
                                <ButtonPreviousNext size="sm" :next="true" @click="step = 3" />
                            </div>
                        </v-row>

                    </v-stepper-window-item>

                    <v-stepper-window-item :value="3">
                        <AttributeLayoutForm  :formValue="FormLayout.Reply" v-if="showFormReply" @add-item="addItem" />
                        <div class="col-12 d-flex justify-start mb-5">
                            <v-btn
                                color="primary"
                                icon
                                class="rounded-circle"
                                v-if="!showFormReply && !disabledInput"
                                @click="showFormReply=true"
                            >
                                <v-icon> mdi-plus</v-icon>
                            </v-btn>
                        </div>
                        <BaseTable
                            :fetchData="null"
                            :item-class="_rowClass"
                            tableToUse="tableColumnsAttributes"
                            :entity="entity"
                            :items="paginatedItemsReply"
                            :loading="loading"
                            :totalItems="totalItemsReply"
                            :currentPage="currentPage"
                            :itemsPerPage="itemsPerPage"
                            :actions="actionsItensList"
                            @delete="handleDelete"
                            @page-change="onPageChange"
                        >
                            <template #clientaccessState="{ row }">
                                <span>
                                    {{ getTranslate(row.clientaccessState) }}
                                </span>
                            </template>
                        </BaseTable>
                        <v-row cols="12" class="mt-10 me-2 ms-2" >
                            <div class="col-6 d-flex justify-start">
                                <ButtonPreviousNext size="sm" :next="false" @click="step = 2" />
                            </div>
                        </v-row>
                        <div class="col-12 d-flex justify-end">
                            <ButtonSave v-if="!disabledInput" :loading="loading" @click="handleSubmit" />
                        </div>
                    </v-stepper-window-item>
                </v-stepper-window>
            </v-stepper>
        </v-card>
    </BasePage>

</template>
